import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {usePeacockSelection} from './usePeacockSelection-hook';

// Stable destructuring defaults — inline `[]`/`() => {}` defaults would
// change identity every render and re-trigger Peacock's effects.
const STABLE_EMPTY_ARRAY = [];
const STABLE_NOOP = () => {};

// Peacock Buttons only accept these variants; anything else misrenders.
const PEACOCK_BUTTON_VARIANTS = new Set([
  'primary',
  'secondary',
  'auxiliary',
  'tertiary',
  'danger'
]);
const toPeacockVariant = (variant) =>
  PEACOCK_BUTTON_VARIANTS.has(variant) ? variant : 'secondary';

/**
 * Encapsulates all business logic for PeacockDataTable: column transformation,
 * stable event handlers, toolbar action building, reset-all, selection model
 * wiring, wrapper-class and select-all summary computation.
 *
 * The component itself is left as a thin render layer that maps the returned
 * values directly onto the Peacock DataTable props.
 */
export const usePeacockDataTable = ({
  columns = [],
  records = [],
  totalRecords = 0,
  paginationConfig = {},
  selectionConfig = {},
  sortingConfig = {},
  filterConfig = {},
  toolbarConfig = {},
  displayConfig = {}
}) => {
  // ── Config destructuring ────────────────────────────────────────────────
  const {
    pageIndex = 0,
    setPageIndex = STABLE_NOOP,
    hidePagination = false,
    onRowsPerPageChange = STABLE_NOOP
  } = paginationConfig;

  const {
    onRowSelect = STABLE_NOOP,
    hideCheckbox = false,
    enableSelectAllPages = false,
    formatSelectAllSummary = null
  } = selectionConfig;

  const {handleSorting = STABLE_NOOP, initialSortConfig = null} = sortingConfig;

  const {
    setSearchArry = STABLE_NOOP,
    onResetAll = STABLE_NOOP,
    hideFilterRow = false,
    showResetAll = false,
    resetAllPosition = 'right',
    resetAllText = 'Reset All',
    disabledHeaders = STABLE_EMPTY_ARRAY
  } = filterConfig;

  const {
    hideToolbar = false,
    toolbarActions = STABLE_EMPTY_ARRAY,
    showDownload = false,
    onDownload = STABLE_NOOP,
    showCreateNew = false,
    onCreateNew = null,
    downloadText = 'Download',
    createNewText = 'Create New',
    showFilterButton = false,
    showMultiSortMenu = false,
    alwaysShowInToolbar = false,
    showActionMenu = false,
    actionItems = STABLE_EMPTY_ARRAY,
    onActionClick = STABLE_NOOP
  } = toolbarConfig;

  const {
    wrapperClassName = '',
    noDataTitle,
    noDataDescription,
    tableSize,
    maxHeight
  } = displayConfig;

  // ── Ref-forwarding: store every prop function so callbacks are stable ───
  const onResetAllRef = useRef(onResetAll);
  const onDownloadRef = useRef(onDownload);
  const handleSortingRef = useRef(handleSorting);
  const setSearchArryRef = useRef(setSearchArry);
  const setPageIndexRef = useRef(setPageIndex);
  const currentPageIndexRef = useRef(pageIndex);
  const onRowsPerPageChangeRef = useRef(onRowsPerPageChange);
  const enableSelectAllPagesRef = useRef(enableSelectAllPages);

  onResetAllRef.current = onResetAll;
  onDownloadRef.current = onDownload;
  handleSortingRef.current = handleSorting;
  setSearchArryRef.current = setSearchArry;
  setPageIndexRef.current = setPageIndex;
  currentPageIndexRef.current = pageIndex;
  onRowsPerPageChangeRef.current = onRowsPerPageChange;
  enableSelectAllPagesRef.current = enableSelectAllPages;

  // ── Selection model ─────────────────────────────────────────────────────
  const {
    selectionUi,
    selectedRowsRef,
    tableRef,
    handleTableReady: selectionTableReady,
    handleRowSelectionUpdate,
    handleFilterChanged,
    clearSelectionModel,
    getDownloadMeta
  } = usePeacockSelection({
    records,
    totalRecords,
    onRowSelect,
    enabled: enableSelectAllPages,
    mode: 'header'
  });

  // Columns that must not show a header filter. Peacock 3.1.3 still enables
  // filtering by default (omitting filterVariant falls back to text), so these
  // keys are also patched onto columnDef.enableColumnFilter after table ready.
  const filterDisabledColumnKeys = useMemo(() => {
    if (hideFilterRow) return STABLE_EMPTY_ARRAY;
    return columns
      .filter((col) => {
        const header =
          col.header_of_column || col.label_of_column || col.header || '';
        return (
          col.filterable === false ||
          disabledHeaders?.includes(col.label_of_column) ||
          disabledHeaders?.includes(col.header) ||
          disabledHeaders?.includes(col.header_of_column) ||
          disabledHeaders?.includes(header)
        );
      })
      .map((col) => col.name_of_db_column || col.columnKey || '')
      .filter(Boolean);
  }, [columns, disabledHeaders, hideFilterRow]);

  // ── Column transformation ───────────────────────────────────────────────
  const peacockColumns = useMemo(() => {
    return columns.map((col) => {
      const columnKey = col.name_of_db_column || col.columnKey || '';
      const header =
        col.header_of_column || col.label_of_column || col.header || '';
      const isDisabled =
        col.filterable === false ||
        disabledHeaders?.includes(col.label_of_column) ||
        disabledHeaders?.includes(col.header) ||
        disabledHeaders?.includes(col.header_of_column) ||
        disabledHeaders?.includes(header);

      const peacockCol = {
        columnKey,
        header,
        enableSorting: col.hideSortIcon ? false : col.enableSorting !== false,
        initialSize: col.minWidth || col.initialSize || 210
      };

      if (col.cellData) peacockCol.cellData = col.cellData;
      if (col.render) peacockCol.render = col.render;
      if (col.linkHandler) peacockCol.linkHandler = col.linkHandler;
      if (col.linkLabel) peacockCol.linkLabel = col.linkLabel;
      if (col.downloadHandler) peacockCol.downloadHandler = col.downloadHandler;
      if (col.downloadLabel) peacockCol.downloadLabel = col.downloadLabel;

      if (!hideFilterRow && !isDisabled) {
        // Config-driven placeholder for every filter variant (Peacock DS uses
        // columnFilterPlaceholder for text, date, dateRange, and dropdown).
        const placeholder =
          col.columnFilterPlaceholder ||
          col.header_of_column ||
          col.label_of_column ||
          header;

        if (
          col.filterVariant === 'select' ||
          col.filterVariant === 'dropdown'
        ) {
          peacockCol.filterVariant = 'dropdown';
          peacockCol.dropdownFilterOptions = {
            selection: 'single',
            dropdownHeader: header
          };
          peacockCol.columnFilterPlaceholder = placeholder;
        } else if (col.filterVariant === 'dateRange') {
          peacockCol.filterVariant = 'dateRange';
          peacockCol.columnFilterPlaceholder = placeholder;
        } else if (col.filterVariant === 'date') {
          peacockCol.filterVariant = 'date';
          peacockCol.columnFilterPlaceholder = placeholder;
        } else {
          peacockCol.filterVariant = 'text';
          peacockCol.columnFilterPlaceholder = placeholder;
        }
      }

      return peacockCol;
    });
  }, [columns, hideFilterRow, disabledHeaders]);

  // ── Stable handlers ─────────────────────────────────────────────────────

  const handleSortingChange = useCallback((sorting) => {
    if (Array.isArray(sorting) && sorting.length > 0) {
      handleSortingRef.current({
        column: sorting[0].column,
        direction: sorting[0].direction
      });
    }
  }, []);

  const handleColumnFiltersChange = useCallback(
    (value, type, columnKey) => {
      if (!columnKey) return;

      let processedValue = value ?? '';

      // Peacock DateRangePicker sends {from: Date, to: Date}.
      // Convert to {from: 'YYYY-MM-DD', to: 'YYYY-MM-DD'} strings.
      if (type === 'dateRange' && value && typeof value === 'object') {
        const toYMD = (d) => {
          if (!d) return '';
          if (d instanceof Date && !Number.isNaN(d.getTime())) {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
          }
          if (typeof d === 'string') {
            const parsed = new Date(d);
            if (!Number.isNaN(parsed.getTime())) {
              const yyyy = parsed.getFullYear();
              const mm = String(parsed.getMonth() + 1).padStart(2, '0');
              const dd = String(parsed.getDate()).padStart(2, '0');
              return `${yyyy}-${mm}-${dd}`;
            }
          }
          return '';
        };
        processedValue = {from: toYMD(value.from), to: toYMD(value.to)};
      }

      // Peacock DatePicker (single date) may send a Date object OR a
      // formatted string (e.g. "08/20/2026") depending on the DS version.
      // Normalize to 'YYYY-MM-DD' so downstream helpers can parse it.
      if (type === 'date' && value) {
        const toYMDFromDate = (d) => {
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
        };

        if (value instanceof Date && !Number.isNaN(value.getTime())) {
          processedValue = toYMDFromDate(value);
        } else if (typeof value === 'string') {
          // Try parsing the formatted string (e.g. "08/20/2026" or other locale formats)
          const parsed = new Date(value);
          if (!Number.isNaN(parsed.getTime())) {
            processedValue = toYMDFromDate(parsed);
          }
        }
      }

      setSearchArryRef.current({
        field: columnKey,
        value: processedValue,
        type: type || 'string'
      });
      // Reset to first page when any column filter changes.
      setPageIndexRef.current(0);
      // Exits select-all only; explicit selections survive filter edits.
      handleFilterChanged();
    },
    [handleFilterChanged]
  );

  // Peacock reports 1-indexed pages — convert to 0-indexed and dedupe.
  const handlePageIndexChange = useCallback((idx) => {
    const zeroIndexed = Math.max(0, idx - 1);
    if (zeroIndexed !== currentPageIndexRef.current) {
      setPageIndexRef.current(zeroIndexed);
    }
  }, []);

  const handleRowsPerPageChange = useCallback((newPageSize) => {
    onRowsPerPageChangeRef.current(newPageSize);
  }, []);

  // Store initialSortConfig in a ref so handleTableReady stays stable.
  const initialSortConfigRef = useRef(initialSortConfig);
  initialSortConfigRef.current = initialSortConfig;

  // Captures the table instance and disables _autoResetPageIndex — with
  // manualPagination the auto-reset fights the externally controlled page.
  const [isTableReady, setIsTableReady] = useState(false);
  // Bumps after enableColumnFilter patches so the filter row re-reads
  // getCanFilter() (Peacock 3.1.3 does not map enableColumnFilter from cols).
  const [, setFilterCapsVersion] = useState(0);

  const handleTableReady = useCallback(
    (table) => {
      selectionTableReady(table);
      if (table && typeof table._autoResetPageIndex === 'function') {
        table._autoResetPageIndex = () => {};
      }
      setIsTableReady(true);
    },
    [selectionTableReady]
  );

  // Sync TanStack's internal pageIndex with the controlled prop whenever
  // pageIndex or records change. Peacock hardcodes autoResetPageIndex: true,
  // so after a page-change triggers a new API call and records update,
  // TanStack resets its internal state to 0. This re-syncs it.
  useEffect(() => {
    if (!isTableReady) return;
    const table = tableRef.current;
    if (table) {
      table.setPageIndex(pageIndex);
    }
  }, [isTableReady, pageIndex, records, tableRef]);

  // Apply initial sorting after the table is ready so the sort icon is
  // visible on first render. Uses a state flag + useEffect instead of
  // setTimeout to stay within the React lifecycle.
  useEffect(() => {
    if (!isTableReady) return;
    const table = tableRef.current;
    const sortCfg = initialSortConfigRef.current;
    if (table && sortCfg?.column) {
      table.setSorting([
        {id: sortCfg.column, desc: sortCfg.direction === 'desc'}
      ]);
    }
  }, [isTableReady, tableRef]);

  // Peacock DS never forwards enableColumnFilter; omit-filterVariant still
  // shows a text filter. Patch TanStack column defs, then re-render once.
  useEffect(() => {
    if (!isTableReady) return;
    const table = tableRef.current;
    if (!table || filterDisabledColumnKeys.length === 0) return;

    let didPatch = false;
    filterDisabledColumnKeys.forEach((key) => {
      const column = table.getColumn(key);
      if (column && column.columnDef.enableColumnFilter !== false) {
        column.columnDef.enableColumnFilter = false;
        didPatch = true;
      }
    });

    if (didPatch) {
      setFilterCapsVersion((v) => v + 1);
    }
  }, [isTableReady, filterDisabledColumnKeys, peacockColumns, tableRef]);

  // ── Toolbar actions ─────────────────────────────────────────────────────
  // Peacock Buttons need `text` (not `label`) and a valid variant.
  const selectedRowsActions = useMemo(() => {
    const actions = [];

    toolbarActions.forEach((action) => {
      actions.push({
        text: action.label || action.displayText || action.text || '',
        variant: toPeacockVariant(action.variant),
        onClick: action.onClick || (() => {})
      });
    });

    if (showDownload && !alwaysShowInToolbar) {
      actions.push({
        text: downloadText,
        variant: 'secondary',
        onClick: () => {
          onDownloadRef.current(selectedRowsRef.current, getDownloadMeta());
        }
      });
    }

    if (showCreateNew && onCreateNew && !alwaysShowInToolbar) {
      actions.push({
        text: createNewText,
        variant: 'secondary',
        onClick: onCreateNew
      });
    }

    return actions;
  }, [
    toolbarActions,
    showDownload,
    downloadText,
    showCreateNew,
    onCreateNew,
    createNewText,
    alwaysShowInToolbar,
    selectedRowsRef,
    getDownloadMeta
  ]);

  // ── Reset All ───────────────────────────────────────────────────────────
  // Peacock's own reset button never renders without global filter chips, so
  // Reset All ships as a toolbar action and resets filters/selection/sorting/
  // page itself through the table instance.
  const handleResetAllInternal = useCallback(() => {
    onResetAllRef.current();
    tableRef.current?.resetColumnFilters();
    if (enableSelectAllPagesRef.current) {
      clearSelectionModel();
    } else {
      tableRef.current?.resetRowSelection();
    }
    tableRef.current?.resetSorting();
    tableRef.current?.setPageIndex(0);
  }, [clearSelectionModel, tableRef]);

  const resetFilterOptions = useMemo(
    () =>
      showResetAll
        ? {onFiltersReset: handleResetAllInternal, text: resetAllText}
        : undefined,
    [showResetAll, resetAllText, handleResetAllInternal]
  );

  const peacockToolbarActions = useMemo(
    () =>
      showResetAll
        ? [
            {
              text: resetAllText,
              variant: 'tertiary',
              onClick: handleResetAllInternal
            }
          ]
        : undefined,
    [showResetAll, resetAllText, handleResetAllInternal]
  );

  const rightSideActionButton = useMemo(() => {
    const actions = [];

    if (alwaysShowInToolbar) {
      if (showCreateNew && onCreateNew) {
        actions.push({
          text: createNewText,
          variant: 'primary',
          onClick: onCreateNew
        });
      }

      if (showDownload) {
        const hasSelection =
          selectionUi.selectedCount > 0 || selectionUi.allMode;
        actions.push({
          text: downloadText,
          variant: 'secondary',
          isDisabled: !hasSelection,
          onClick: () => {
            onDownloadRef.current(selectedRowsRef.current, getDownloadMeta());
          }
        });
      }
    }

    return actions.length > 0 ? actions : undefined;
  }, [
    alwaysShowInToolbar,
    showCreateNew,
    onCreateNew,
    createNewText,
    showDownload,
    downloadText,
    selectionUi,
    selectedRowsRef,
    getDownloadMeta
  ]);

  const actionMenuConfig = useMemo(
    () =>
      showActionMenu
        ? {actionItems, onActionClick, selectedCount: selectionUi.selectedCount}
        : undefined,
    [showActionMenu, actionItems, onActionClick, selectionUi.selectedCount]
  );

  // ── Presentation ────────────────────────────────────────────────────────
  const selectionMode = hideCheckbox ? 'none' : 'multiple';

  const hasLeftToolbarItems =
    (showFilterButton && !hideFilterRow) || showMultiSortMenu;

  const wrapperClasses = [
    'peacock-datatable-wrapper',
    'relative',
    !hasLeftToolbarItems ? 'no-toolbar-divider' : '',
    resetAllPosition === 'left' ? 'toolbar-actions-left' : '',
    selectionUi.allMode ? 'select-all-active' : '',
    wrapperClassName
  ]
    .filter(Boolean)
    .join(' ');

  // While select-all is armed, the accurate dataset count replaces the
  // library's page-scoped label via the --select-all-summary CSS variable.
  // The last positive total is held so a mid-refetch totalRecords=0 never
  // renders a zero-count summary.
  const lastPositiveTotalRef = useRef(0);
  if (totalRecords > 0) {
    lastPositiveTotalRef.current = totalRecords;
  }

  let selectAllSummary = null;
  if (selectionUi.allMode) {
    const summaryTotal =
      totalRecords > 0 ? totalRecords : lastPositiveTotalRef.current;
    if (summaryTotal > 0) {
      const excludedCount = selectionUi.excludedCount;
      const effectiveCount = Math.max(summaryTotal - excludedCount, 0);
      if (typeof formatSelectAllSummary === 'function') {
        selectAllSummary = formatSelectAllSummary({
          totalRecords: summaryTotal,
          excludedCount,
          effectiveCount
        });
      } else if (excludedCount > 0) {
        selectAllSummary = `${effectiveCount} of ${summaryTotal} selected`;
      } else {
        selectAllSummary = `${summaryTotal} records selected`;
      }
    }
  }

  return {
    // Columns
    peacockColumns,
    // Handlers
    handleSortingChange,
    handleColumnFiltersChange,
    handlePageIndexChange,
    handleRowsPerPageChange,
    handleTableReady,
    handleRowSelectionUpdate,
    // Toolbar
    selectedRowsActions,
    peacockToolbarActions,
    rightSideActionButton,
    actionMenuConfig,
    resetFilterOptions,
    // Selection
    selectionMode,
    selectionUi,
    // Presentation
    wrapperClasses,
    selectAllSummary,
    // Pass-through config values consumed directly in the render
    pageIndex,
    hidePagination,
    hideFilterRow,
    hideToolbar,
    showFilterButton,
    showMultiSortMenu,
    noDataTitle,
    noDataDescription,
    tableSize,
    maxHeight
  };
};
